"use client";

import * as React from "react";
import { cn } from "../cn";
import * as PopoverPrimitive from "@radix-ui/react-popover";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

interface PopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  /**
   * When true, portals to body instead of trying to find a dialog container.
   * Use this when the Popover is inside a Dialog to avoid container detection issues.
   */
  portalToBody?: boolean;
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = "center",
      sideOffset = 4,
      portalToBody = false,
      ...props
    },
    ref
  ) => {
    const container = React.useMemo<HTMLElement | undefined>(() => {
      if (portalToBody || typeof window === "undefined") return undefined;
      const dialogContent = document.querySelector(
        '[data-dialog-content][data-state="open"]'
      ) as HTMLElement | null;
      return dialogContent ?? undefined;
    }, [portalToBody]);

    return (
      <PopoverPrimitive.Portal container={portalToBody ? undefined : container}>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            "z-[1000]",
            "w-72",
            "rounded-md",
            "border",
            "bg-surface",
            "p-4",
            "text-popover-foreground",
            "shadow-md",
            "outline-none",
            "data-[state=open]:animate-in",
            "data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0",
            "data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95",
            "data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2",
            "data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2",
            "data-[side=top]:slide-in-from-bottom-2",
            "origin-[--radix-popover-content-transform-origin]",
            className
          )}
          {...props}
        />
      </PopoverPrimitive.Portal>
    );
  }
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
